import React, { useRef, useState } from "react";
import { flushSync } from "react-dom";
import { C, font, btnP, btnS } from "../../../lib/styles.js";
import { addendumImages, printWordingWarnings } from "../../../lib/builderHelpers.js";
import { resolveAddendumImageSrc } from "../../../lib/attachmentBytes.js";
import { getImages } from "../../../lib/imageStorage.js";
import QuotePrintable from "./QuotePrintable.jsx";
// Imported statically, NOT via await import(). As a lazy chunk this was the only
// code in the app fetched from the network after boot, so every `vercel --prod`
// broke Download PDF in any already-open tab: the old bundle asked for its old
// pdfExport-<hash>.js, which no longer exists, and the SPA rewrite answered with
// index.html — "'text/html' is not a valid JavaScript MIME type" (Safari, 2026-09-02).
// Bundling it in costs ~176 kB gzip at load and makes a stale tab merely stale
// instead of broken. Do not make this dynamic again.
import { exportQuotePdf } from "../../../lib/pdfExport.js";

/*
  QuotePdfPreview — full-screen overlay that shows the client-facing quote
  document (QuotePrintable) and exports it to PDF via html2pdf, mirroring the
  legacy preview-step workflow.
*/
export default function QuotePdfPreview({ quote, onClose, onDone, onIngest, canIngest = false, ingestLabel = "Ingest into Quotes →", ingestDisabledReason = "", closeLabel = "← Edit", notice = "", lock = null }) {
  // The lock's own state (lib/quotePersistence.js): an ingest that passed its time limit
  // says so here, since this preview covers the builder's message.
  const stuck = lock?.stuck ? lock.message : "";
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  // F15: id → base64 dataURL map for addendum images, resolved at export time so the
  // PDF embeds them instead of cross-origin Storage URLs (null = on-screen preview path).
  const [exportImages, setExportImages] = useState(null);
  // Surfaced when one or more addendum images can't be resolved — without this the
  // PDF exports "successfully" with images silently missing (the original K1 symptom,
  // just moved to the failure path). The operator decides: retry or send anyway.
  const [exportWarning, setExportWarning] = useState("");
  // A failed export used to be console-only: the button just went back to idle and
  // the operator had no idea the download never happened. Surfaced on the bar now.
  const [exportError, setExportError] = useState("");
  // Resolved dataURLs are kept across repeat downloads (no re-fetch of multi-MB
  // images on a second click); the STATE is cleared after each save so the on-screen
  // preview renders lightweight srcs again instead of giant base64 strings.
  const resolvedRef = useRef({});
  // Ingest (commit to the Quotes queue) lives here, on the review gate. Owns its
  // own busy/done/error so the operator stays on the preview after ingesting and
  // can still Download. onIngest throws on failure.
  const [ingest, setIngest] = useState("idle"); // idle | saving | done | error
  const [ingestErr, setIngestErr] = useState("");

  const filename = `${(quote.clientName || "quote").replace(/[^a-z0-9]+/gi, "_")}_${(quote.quoteDate || "").replace(/-/g, "")}`.replace(/_+$/, "") || "quote";

  const download = async () => {
    if (!ref.current || busy) return;
    setBusy(true);
    setExportWarning("");
    setExportError("");
    setIngestErr("");
    try {
      // F15: embed every addendum image as a base64 dataURL BEFORE html2pdf rasterizes.
      // html2canvas silently drops cross-origin Storage URLs (no CORS headers), so a
      // reopened quote whose in-memory dataUrl is gone would export with the addendum
      // missing. Resolve per image: in-memory dataUrl → IndexedDB cache → fetch the
      // Storage url → base64, then commit synchronously (flushSync) so the rendered
      // <img> srcs are embedded dataURLs at capture time.
      const addenda = addendumImages(quote.attachments);
      if (addenda.length) {
        let cache = null;
        try { cache = await getImages(quote.id); } catch { cache = null; } // { attId: dataUrl }
        const entries = await Promise.all(addenda.map(async (att) => {
          if (resolvedRef.current[att.id]) return [att.id, resolvedRef.current[att.id]];
          const hydrated = att.dataUrl ? att : (cache && cache[att.id] ? { ...att, dataUrl: cache[att.id] } : att);
          const src = await resolveAddendumImageSrc(hydrated);
          if (src) resolvedRef.current[att.id] = src;
          return src ? [att.id, src] : null;
        }));
        const resolved = entries.filter(Boolean);
        // An image that can't be resolved would fall back to its cross-origin Storage
        // url in QuotePrintable — which html2canvas drops — so the export would look
        // fine on screen and quietly ship without it. Say so, loudly.
        const missing = addenda.length - resolved.length;
        if (missing > 0) {
          setExportWarning(`${missing} reference image${missing === 1 ? "" : "s"} couldn't be loaded and will be MISSING from this PDF — check your connection and download again before sending it.`);
        }
        flushSync(() => setExportImages(Object.fromEntries(resolved)));
      }

      // Deterministic composer (src/lib/pdfExport.js): one [data-quote-page] div per
      // PDF page, sliced only for the variable-height quote page at safe cut lines.
      // html2pdf's automatic pagination is gone — its spacer math rounds differently
      // in Safari vs Chrome and produced blank/drifted pages (Freefall 2026-07-02).
      await exportQuotePdf(ref.current, filename);
    } catch (e) {
      console.error("PDF export failed:", e);
      setExportError(`PDF export failed — nothing was downloaded. ${e?.message || e}`);
    } finally {
      // Drop the embedded base64 map once the capture is done — the on-screen preview
      // goes back to lightweight dataUrl/Storage srcs (resolvedRef keeps the resolved
      // bytes for the next click without holding them in rendered state).
      setExportImages(null);
      setBusy(false);
    }
  };

  const runIngest = async () => {
    if (!onIngest || !canIngest || ingest === "saving" || ingest === "done") return;
    setIngest("saving");
    setIngestErr("");
    setExportWarning("");
    setExportError("");
    try {
      await onIngest();
      setIngest("done");
    } catch (e) {
      setIngest("error");
      setIngestErr(e?.message || "Could not ingest this quote.");
    }
  };

  return (
    <div style={S.overlay} onClick={onClose}>
      <div style={S.bar} onClick={e => e.stopPropagation()}>
        <span style={S.barTitle}>
          {exportError
            ? <span style={S.err}>{exportError}</span>
            : exportWarning
            ? <span style={S.warn}>⚠ {exportWarning}</span>
            : ingestErr
            ? <span style={S.err}>{ingestErr}</span>
            : "Quote PDF — preview (what the client receives)"}
        </span>
        <div style={S.barActions}>
          <button type="button" style={btnS} onClick={onClose}>{closeLabel}</button>
          <button type="button" style={{ ...btnS, opacity: busy ? 0.5 : 1 }} disabled={busy} onClick={download}>
            {busy ? "Generating…" : "⬇ Download PDF"}
          </button>
          {onIngest && (
            <button
              type="button"
              style={{ ...btnP, opacity: (!canIngest || ingest === "saving" || ingest === "done") ? 0.5 : 1 }}
              disabled={!canIngest || ingest === "saving" || ingest === "done"}
              title={!canIngest ? ingestDisabledReason : undefined}
              onClick={runIngest}
            >
              {ingest === "saving" ? "Ingesting…" : ingest === "done" ? "✓ Ingested" : ingestLabel}
            </button>
          )}
          {/* Post-ingest exit. Before ingest the only labeled exit is "← Edit" (back
              to the builder) plus the overlay-bg click; once the quote is committed
              the operator needs an explicit way OUT to the quotes list. Done routes
              there (onDone) and falls back to onClose if no list route is wired. */}
          {ingest === "done" && (
            <button type="button" style={btnP} onClick={onDone || onClose}>
              Done
            </button>
          )}
        </div>
      </div>
      <div style={S.scroll} onClick={e => e.stopPropagation()}>
        {notice && <div data-testid="pdf-saved-notice" style={S.notice}>{notice}</div>}
        {stuck && <div data-testid="pdf-stuck" style={{ ...S.notice, borderColor: C.danger, color: C.danger }}>{stuck}</div>}
        {printWordingWarnings(quote).map((w, i) => <div key={i} data-testid="pdf-wording-warning" style={S.notice}>{w}</div>)}
        <div ref={ref} style={S.sheetWrap}>
          <QuotePrintable quote={quote} imageSrc={exportImages} />
        </div>
      </div>
    </div>
  );
}

const S = {
  notice: { maxWidth: 816, margin: "0 auto 12px", background: C.surface, border: `1px solid ${C.amber}`, borderRadius: 6, padding: "10px 13px", fontSize: 13, lineHeight: 1.5, color: C.text },
  overlay: { position: "fixed", inset: 0, zIndex: 1000, background: "rgba(40,40,42,0.92)", display: "flex", flexDirection: "column", fontFamily: font },
  bar: { display: "flex", justifyContent: "space-between", alignItems: "center", padding: "14px 22px", borderBottom: "1px solid rgba(255,255,255,0.12)" },
  barTitle: { color: "#ddd", fontSize: 13, letterSpacing: "0.03em" },
  err: { color: "#ff9b9b", fontSize: 12.5 },
  warn: { color: "#ffd089", fontSize: 12.5 },
  barActions: { display: "flex", gap: 10 },
  scroll: { flex: 1, overflow: "auto", padding: "28px 16px", display: "flex", flexDirection: "column", alignItems: "center", gap: 20 },
  sheetWrap: { display: "flex", flexDirection: "column", gap: 20, boxShadow: "0 8px 60px rgba(0,0,0,0.5)" },
};
